import React, { Component } from 'react';
import FlashMessage from '~/FlashMessage';
import Navigation from '~/UI/Navigation/Navigation';

import styles from './styles.less';

interface Props {
	children: React.ReactNode;
	navigation: React.ReactNode;
	navigationPanel: React.ReactNode;
	bottomNavigationPanel: React.ReactNode;
	hasPanel: boolean;
	spacedOut: boolean;
	/**
	 * Makes the page itself the scroll container instead of reserving a fixed band
	 * for the navigation. The navigation then scrolls with the page and pins to the
	 * top, until a `sticky` toolbar further down reaches the top and takes over.
	 * Pages opting in must not carry their own `h-full overflow-auto` scroller.
	 */
	scrollsWithContent?: boolean;
}

export default class Layout extends Component<Props> {
	static defaultProps = {
		navigation: null,
		navigationPanel: null,
		bottomNavigationPanel: null,
		spacedOut: false,
		hasPanel: false,
	};

	private pageRef = React.createRef<HTMLDivElement>();

	private frame: number | null = null;

	componentDidMount() {
		const page = this.pageRef.current;

		if (!this.props.scrollsWithContent || !page) {
			return;
		}

		page.addEventListener('scroll', this.handleScroll, { passive: true });
		this.updateNavigationShift();
	}

	componentWillUnmount() {
		this.pageRef.current?.removeEventListener('scroll', this.handleScroll);

		if (this.frame !== null) {
			cancelAnimationFrame(this.frame);
		}
	}

	render() {
		const { children, navigation, navigationPanel, bottomNavigationPanel, hasPanel, scrollsWithContent } = this.props;

		return (
			<div ref={this.pageRef} className={this.resolveClassName()}>
				<Navigation
					bottomPanel={hasPanel ? bottomNavigationPanel : null}
					panel={navigationPanel}
					inFlow={scrollsWithContent}
				>
					{navigation}
				</Navigation>
				{children}
				<FlashMessage />
			</div>
		);
	}

	private handleScroll = () => {
		if (this.frame !== null) {
			return;
		}

		this.frame = requestAnimationFrame(() => {
			this.frame = null;
			this.updateNavigationShift();
		});
	};

	// Offsets the navigation by the scroll position so it travels with the viewport,
	// but never past the page's sticky toolbar. Once the toolbar reaches it the offset
	// stops growing, so the navigation scrolls out of view rather than sitting behind
	// the toolbar. Pages opt in by marking their sticky region with data-sticky-toolbar.
	private updateNavigationShift() {
		const page = this.pageRef.current;
		const navigation = page?.querySelector<HTMLElement>('[data-app-navigation]');
		const toolbar = page?.querySelector<HTMLElement>('[data-sticky-toolbar]');

		if (!page || !navigation) {
			return;
		}

		if (!toolbar) {
			page.style.setProperty('--nav-shift', `${page.scrollTop}px`);
			return;
		}

		// The toolbar wrapper is not itself sticky, so its box still reports the
		// position the sticky bar inside it scrolls up from.
		const toolbarOffset = toolbar.getBoundingClientRect().top - page.getBoundingClientRect().top + page.scrollTop;
		const limit = Math.max(toolbarOffset - navigation.offsetHeight, 0);

		page.style.setProperty('--nav-shift', `${Math.min(page.scrollTop, limit)}px`);
	}

	resolveClassName(): string {
		const { hasPanel, spacedOut, bottomNavigationPanel, scrollsWithContent } = this.props;

		if (scrollsWithContent) {
			return spacedOut ? styles.spacedOutScrollingPage : styles.scrollingPage;
		}
		if (hasPanel && spacedOut && bottomNavigationPanel) {
			return styles.spacedOutPageWithPanel;
		}
		if (hasPanel && bottomNavigationPanel) {
			return styles.pageWithPanel;
		}
		if (spacedOut) {
			return styles.spacedOutPage;
		}
		return styles.page;
	}
}
